/**
 * Delivery History Detail API
 * 배달 상세 내역 조회 API
 *
 * @description
 * 배달 상세 내역 조회, 협력사/라이더 목록 조회, 엑셀 다운로드 기능 제공
 */
import { apiClient } from '@/shared/api/client';
import { logger } from '@/shared/utils/logger';
import {
    extractArrayFromResponse,
    toSafeString,
    formatDateToYmd,
} from "@/shared/utils/apiResponseHelpers";
import type {
    DeliveryHistoryDetailFilters,
    DeliveryHistoryDetailResponse,
    DeliverySupplier,
    DeliverySupplierListResponse,
    DeliverySupplierRawItem,
    DeliveryRider,
    DeliveryRiderListResponse,
    DeliveryRiderRawItem,
} from '../types/deliveryHistoryDetail.types';

// ============================================================
// 상수 정의
// ============================================================

/** 배달 내역 API 기본 경로 */
const BASE_PATH = '/api/v1/delivery/history';

/** 배달 API 기본 경로 */
const DELIVERY_BASE_PATH = '/api/v1/delivery';

/** 엑셀 다운로드 타임아웃 (5분, 밀리초) */
const EXCEL_DOWNLOAD_TIMEOUT = 300000;

// ============================================================
// 헬퍼 함수
// ============================================================

/**
 * 날짜 필터 파라미터 생성
 * YYYY-MM-DD 형식을 YYYYMMDD 형식으로 변환
 *
 * @param filters 필터 조건
 * @returns API 파라미터 객체
 */
const buildDateParams = (
    filters: DeliveryHistoryDetailFilters
): Record<string, string | undefined> => ({
    suppId: filters.suppId,
    riderId: filters.riderId || undefined,
    fromDate: filters.startDate ? formatDateToYmd(filters.startDate) : undefined,
    toDate: filters.endDate ? formatDateToYmd(filters.endDate) : undefined,
});

/**
 * 협력사 데이터 정규화
 */
const normalizeSupplier = (item: DeliverySupplierRawItem): DeliverySupplier => ({
    suppId: toSafeString(item.suppId),
    suppName: item.suppName ?? '',
});

/**
 * 라이더 데이터 정규화
 */
const normalizeRider = (item: DeliveryRiderRawItem): DeliveryRider => ({
    riderId: toSafeString(item.riderId),
    riderName: item.riderName ?? '',
});

// ============================================================
// API 서비스
// ============================================================

/**
 * 배달 상세 내역 API 서비스
 */
export const deliveryHistoryDetailApi = {
    /**
    * 배달 내역 상세 조회
    * GET /api/v1/delivery/history/fee-list
    * @param filters 조회 조건
    * @returns 배달 상세 내역 응답
    */
    async getDetails(filters: DeliveryHistoryDetailFilters): Promise<DeliveryHistoryDetailResponse> {

        const params = buildDateParams(filters);
        const url = `${BASE_PATH}/fee-list`;

        logger.apiRequest('GET', url, params);

        try {
            const response = await apiClient.get<DeliveryHistoryDetailResponse>(url, { params });
            logger.apiResponse('GET', url, response.status, response.data);
            return response.data;
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },

    /**
     * 협력사 목록 조회
     * GET /api/v1/delivery/supplier/list
     *
     * @returns 협력사 목록
     */
    async getSuppliers(): Promise<DeliverySupplier[]> {
        const url = `${DELIVERY_BASE_PATH}/supplier/list`;
        logger.apiRequest('GET', url);

        try {
            const response = await apiClient.get<DeliverySupplierListResponse>(url);
            logger.apiResponse('GET', url, response.status, response.data);

            const items = extractArrayFromResponse<DeliverySupplierRawItem>(
                response.data as DeliverySupplierRawItem[] | { suppliers?: DeliverySupplierRawItem[]; items?: DeliverySupplierRawItem[]; data?: DeliverySupplierRawItem[] },
                'suppliers'
            );

            return items
                .map(normalizeSupplier)
                .filter((item) => item.suppId && item.suppName);
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },

    /**
     * 라이더 목록 조회
     * GET /api/v1/delivery/rider/list
     * @param suppId 협력사 ID
     * @returns 라이더 목록
     */
    async getRiders(suppId: string): Promise<DeliveryRider[]> {
        if (!suppId) {
            return [];
        }

        const url = `${DELIVERY_BASE_PATH}/rider/list`;
        const params = { suppId };
        logger.apiRequest('GET', url, params);

        try {
            const response = await apiClient.get<DeliveryRiderListResponse>(url, { params });
            logger.apiResponse('GET', url, response.status, response.data);

            const items = extractArrayFromResponse<DeliveryRiderRawItem>(
                response.data as DeliveryRiderRawItem[] | { riders?: DeliveryRiderRawItem[]; items?: DeliveryRiderRawItem[]; data?: DeliveryRiderRawItem[] },
                'riders'
            )

            return items
                .map(normalizeRider)
                .filter((item) => item.riderId && item.riderName);
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },

    /**
    * 배달 내역 상세 엑셀 다운로드
    * @param filters 조회 조건
    */
    async downloadExcel(filters: DeliveryHistoryDetailFilters): Promise<Blob> {
        const params = {
            suppId: filters.suppId,
            riderId: filters.riderId || undefined,
            fromDate: filters.startDate ? filters.startDate.replace(/-/g, '') : undefined,
            toDate: filters.endDate ? filters.endDate.replace(/-/g, '') : undefined,
        };

        const url = `${BASE_PATH}/fee-list/excel`;
        logger.apiRequest('GET', url, params);

        try {
            const response = await apiClient.get<Blob>(url, {
                params,
                responseType: 'blob',
                timeout: EXCEL_DOWNLOAD_TIMEOUT,
            });

            logger.apiResponse('GET', url, response.status, 'Blob');
            return response.data;
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },
};
